import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { merkeKaufSprache } from './KaufHinweis';
import { zustimmungFelder } from './zustimmung';
import './kauf.css';
import './abo-zustimmung.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Zustimmung vor der Abo-Kasse (Prüfbericht Fund H6). Jeder Knopf, der das All-in-One startet
// („7 Tage kostenlos testen“ auf der All-in-One-Seite oben und unten, im All-in-One-Band von Startseite,
// Werkzeuge- und Kostenlos-Seite und auf den Produktseiten), öffnet zuerst diesen Dialog statt direkt
// zu Stripe zu springen. Darin: kurze Zusammenfassung (Laufzeit, Preis, 7 Tage kostenlos, danach
// Abbuchung, Kündigung) und dieselben zwei Pflicht-Häkchen wie im Warenkorb (CartSidebar.jsx, Wortlaut
// und Links von dort übernommen). Erst wenn beide gesetzt sind, geht „Weiter zur Kasse“: dann wie bisher
// merkeKaufSprache + create-checkout mit abo-jahr bzw. abo-monat, dazu der Nachweis der beiden Häkchen
// (zustimmung.js: server.py speichert ihn am Abo, die Kauf-Mail bestätigt ihn).
//
// Benutzung:  <AboZustimmung offen={…} plan="jahr"|"monat" en={en} onZu={…} onPlan={…optional} />
// onPlan meldet einen Wechsel Monatlich/Jährlich im Dialog zurück (die All-in-One-Seite stellt dann
// ihren eigenen Umschalter mit um).

const ABO_JAHR = 159, ABO_MONAT = 15.99;
// Wartet die Kasse länger als das, gilt es als Fehler (sonst hinge der Knopf ewig auf „Öffnet…“)
const WARTEZEIT_MS = 20000;

const euro = (en, v, stellen = 2) => {
  const z = v.toFixed(stellen);
  return en ? '€' + z : z.replace('.', ',') + ' €';
};

// Alles, was im Dialog per Tab erreichbar ist (für die Fokus-Falle)
const FOKUSSIERBAR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Pflicht-Häkchen wie im Warenkorb: dunkles Kästchen, angehakt mit orangem Rand und orangem Haken.
// Das echte <input> bleibt für Tastatur, Screenreader und Tests.
const Haekchen = ({ checked, onChange, testId }) => (
  <span className="az-kaestchen">
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testId} />
    <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
  </span>
);

export default function AboZustimmung({ offen, plan = 'jahr', en, onZu, onPlan }) {
  const [wahl, setWahl] = useState(plan === 'monat' ? 'monat' : 'jahr');
  // § 356 Abs. 6 Nr. 2 BGB: ausdrückliche Zustimmung zum Beginn der Ausführung vor Ablauf der Widerrufsfrist
  // und Kenntnis vom Erlöschen des Widerrufsrechts — wie im Warenkorb
  const [agb, setAgb] = useState(false);
  const [verzicht, setVerzicht] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(false);
  const fenster = useRef(null);
  const titel = useRef(null);
  const vorher = useRef(null);
  const abbruch = useRef(null);
  const T = (de, enText) => (en ? enText : de);
  const p = en ? '/en' : '';
  const beide = agb && verzicht;

  // Bei jedem Öffnen frisch: Laufzeit vom Knopf, Häkchen leer (die Zustimmung gilt für diesen einen Kauf)
  useEffect(() => {
    if (!offen) return;
    setWahl(plan === 'monat' ? 'monat' : 'jahr');
    setAgb(false); setVerzicht(false); setLaeuft(false); setFehler(false);
  }, [offen]); // nur beim Öffnen: ein Wechsel der Laufzeit im Dialog soll nichts zurücksetzen

  const zu = () => {
    if (abbruch.current) { abbruch.current.abort(); abbruch.current = null; }
    onZu && onZu();
  };
  // Aktuelle Fassung für die Tasten-Abfrage, ohne den Effekt bei jedem Rendern neu zu binden
  const zuRef = useRef(zu);
  zuRef.current = zu;

  // Offen: Seite dahinter steht still, Fokus auf die Überschrift (so wird die Zusammenfassung zuerst
  // vorgelesen), Esc schließt, Tab bleibt im Dialog. Zu: Fokus zurück auf den Knopf, der geöffnet hat.
  useEffect(() => {
    if (!offen) return undefined;
    vorher.current = document.activeElement;
    const html = document.documentElement;
    const alterUeberlauf = html.style.overflow;
    html.style.overflow = 'hidden';
    const t = setTimeout(() => titel.current && titel.current.focus({ preventScroll: true }), 60);
    const taste = e => {
      if (e.key === 'Escape') { e.preventDefault(); zuRef.current(); return; }
      if (e.key !== 'Tab' || !fenster.current) return;
      const ziele = [...fenster.current.querySelectorAll(FOKUSSIERBAR)].filter(el => el.offsetParent !== null);
      if (!ziele.length) return;
      const erstes = ziele[0], letztes = ziele[ziele.length - 1];
      const drin = fenster.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === erstes || !drin || document.activeElement === titel.current)) { e.preventDefault(); letztes.focus(); }
      else if (!e.shiftKey && (document.activeElement === letztes || !drin)) { e.preventDefault(); erstes.focus(); }
    };
    // Zurück aus der Stripe-Kasse per Browser-Zurück (Seite aus dem Zwischenspeicher): Knopf wieder frei
    const zurueck = e => { if (e.persisted) setLaeuft(false); };
    window.addEventListener('keydown', taste);
    window.addEventListener('pageshow', zurueck);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', taste);
      window.removeEventListener('pageshow', zurueck);
      html.style.overflow = alterUeberlauf;
      const el = vorher.current;
      if (el && el.focus && document.contains(el)) el.focus({ preventScroll: true });
    };
  }, [offen]);

  const waehle = w => { setWahl(w); onPlan && onPlan(w); };

  // Abo läuft nicht über den Warenkorb — Stripe mischt Abonnement und Einmalzahlung nicht in einer
  // Sitzung. Direkt zur Kasse, aber erst mit beiden Häkchen.
  const zurKasse = async () => {
    if (!beide || laeuft) return;
    setLaeuft(true);
    setFehler(false);
    // Stripe schickt immer auf die deutsche Adresse zurück — KaufHinweis springt dann auf /en, wenn es englisch war
    merkeKaufSprache(en);
    const steuerung = new AbortController();
    abbruch.current = steuerung;
    const wecker = setTimeout(() => steuerung.abort(), WARTEZEIT_MS);
    try {
      const r = await fetch(`${API_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: wahl === 'jahr' ? 'abo-jahr' : 'abo-monat',
          origin_url: window.location.origin,
          ...zustimmungFelder({ agb, verzicht, en }),
        }),
        signal: steuerung.signal,
      });
      const d = await r.json();
      if (r.ok && d.url) { window.location.href = d.url; return; }
      throw new Error('keine Kasse');
    } catch (e) {
      // Selbst geschlossen: still bleiben
      if (abbruch.current !== steuerung) return;
      setFehler(true);
      setLaeuft(false);
    } finally {
      clearTimeout(wecker);
      if (abbruch.current === steuerung) abbruch.current = null;
    }
  };

  const jahr = wahl === 'jahr';
  const betrag = jahr ? euro(en, ABO_JAHR, 0) : euro(en, ABO_MONAT);
  const danach = jahr ? T(`${betrag} im Jahr`, `${betrag} a year`) : T(`${betrag} im Monat`, `${betrag} a month`);

  return createPortal(
    <AnimatePresence>
      {offen && (
        <motion.div
          key="abo-zustimmung"
          className="kauf-schleier az-schleier"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.25 } }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
          onClick={e => { if (e.target === e.currentTarget) zu(); }}
          // Lenis (SmoothScroll) lässt Mausrad und Wischen hier in Ruhe: der Dialog scrollt selbst
          data-lenis-prevent=""
          data-testid="abo-zustimmung"
          data-plan={wahl}
        >
          <motion.div
            ref={fenster}
            className="kauf-hinweis az"
            role="dialog"
            aria-modal="true"
            aria-labelledby="abo-zustimmung-titel"
            aria-describedby="abo-zustimmung-text"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } }}
            exit={{ opacity: 0, y: 12, transition: { duration: 0.2 } }}
          >
            <div className="kh-glut" aria-hidden="true" />
            <button type="button" className="kh-zu" onClick={zu} aria-label={T('Schließen', 'Close')} data-testid="abo-zustimmung-zu">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <div className="kh-kopf">
              <span className="kh-zeichen" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="4" y="5.5" width="16" height="14.5" rx="2.5" /><path d="M8 3.5v4M16 3.5v4M4 10h16M9 14.6l2 2 4-4" /></svg>
              </span>
              <span className="kh-kicker">{T('All‑in‑One · Abo', 'All‑in‑One · subscription')}</span>
            </div>
            <h2 id="abo-zustimmung-titel" ref={titel} tabIndex={-1}>{T('7 Tage kostenlos testen.', 'Start your 7-day free trial.')}</h2>

            <div id="abo-zustimmung-text">
              <p>{T('Studio Flow mit allen Werkzeugen in einem Panel, dazu Instant Grade, Instant Comp und jedes neue Werkzeug.',
                'Studio Flow with every tool in one panel, plus Instant Grade, Instant Comp and every new tool.')}</p>

              <div className="az-box">
                <div className="az-oben">
                  <div className="az-wahl" role="radiogroup" aria-label={T('Laufzeit', 'Billing period')}>
                    {[['monat', T('Monatlich', 'Monthly')], ['jahr', T('Jährlich', 'Yearly')]].map(([w, name]) => (
                      <button key={w} type="button" role="radio" aria-checked={wahl === w} className={wahl === w ? 'an' : ''}
                        onClick={() => waehle(w)} data-testid={`abo-zustimmung-${w}`}>{name}</button>
                    ))}
                  </div>
                  <div className="az-preis">
                    <div><b>{betrag}</b> <small>{jahr ? T('/ Jahr', '/ year') : T('/ Monat', '/ month')}</small></div>
                    <span>{jahr
                      ? T(`${euro(en, ABO_JAHR / 12)} im Monat · 2 Monate geschenkt`, `${euro(en, ABO_JAHR / 12)} a month · 2 months free`)
                      : T('monatlich kündbar', 'cancel any month')}</span>
                  </div>
                </div>
                <ul className="az-punkte">
                  <li><b>{T('Heute 0 €:', '€0 today:')}</b> {T('die ersten 7 Tage sind kostenlos.', 'the first 7 days are free.')}</li>
                  <li><b>{T(`Danach ${danach},`, `Then ${danach},`)}</b> {T('automatisch abgebucht und verlängert, bis du kündigst.', 'charged automatically and renewed until you cancel.')}</li>
                  <li><b>{T('Jederzeit kündbar', 'Cancel any time,')}</b> {T('zum Ende der Laufzeit. Kündigst du in den 7 Tagen, wird nichts abgebucht.', 'effective at the end of the current term. Cancel within the 7 days and nothing is charged.')}</li>
                </ul>
              </div>
            </div>

            {/* Pflicht-Häkchen: Wortlaut und Links wie im Warenkorb (CartSidebar.jsx) */}
            <div className="az-haken">
              <label>
                <Haekchen checked={agb} onChange={e => setAgb(e.target.checked)} testId="abo-accept-terms" />
                <span>
                  {en ? (
                    <>I have read and accept the{' '}<a href="/en/terms" target="_blank" rel="noopener">Terms</a>{' '}and the{' '}<a href="/en/withdrawal" target="_blank" rel="noopener">Withdrawal Policy</a>.</>
                  ) : (
                    <>Ich habe die{' '}<a href="/agb" target="_blank" rel="noopener">AGB</a>{' '}und die{' '}<a href="/widerruf" target="_blank" rel="noopener">Widerrufsbelehrung</a>{' '}gelesen und akzeptiere sie.</>
                  )}
                </span>
              </label>
              <label>
                <Haekchen checked={verzicht} onChange={e => setVerzicht(e.target.checked)} testId="abo-waive-withdrawal" />
                <span>
                  {en ? (
                    <>I expressly consent to performance of the contract beginning before the withdrawal period expires and acknowledge that{' '}<strong>my right of withdrawal expires</strong> upon download being made available (§&nbsp;356&nbsp;(6) no.&nbsp;2 BGB).</>
                  ) : (
                    <>Ich stimme ausdrücklich zu, dass mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist begonnen wird und bestätige, dass{' '}<strong>mein Widerrufsrecht erlischt</strong>, sobald der Download bereitgestellt wird (§&nbsp;356 Abs.&nbsp;6 Nr.&nbsp;2 BGB).</>
                  )}
                </span>
              </label>
            </div>

            {fehler && (
              <p className="az-fehler" role="alert" data-testid="abo-zustimmung-fehler">
                {T('Die Kasse ließ sich gerade nicht öffnen, es wurde nichts abgebucht. Bitte versuch es gleich noch einmal. Klappt es weiter nicht, ',
                  'The checkout couldn’t be opened just now, nothing was charged. Please try again in a moment. If it still doesn’t work, ')}
                <Link to={`${p}/support`} onClick={zu}>{T('schreib uns über den Support', 'contact our support')}</Link>.
              </p>
            )}

            <div className="kh-knoepfe">
              <button type="button" className="kh-btn primaer" onClick={zurKasse} disabled={!beide || laeuft}
                aria-describedby={beide ? undefined : 'abo-zustimmung-hinweis'} data-testid="abo-zur-kasse">
                {laeuft ? T('Öffnet…', 'Opening…') : T('Weiter zur Kasse', 'Continue to checkout')}
              </button>
              <button type="button" className="kh-btn glas" onClick={zu} data-testid="abo-zustimmung-abbrechen">{T('Abbrechen', 'Cancel')}</button>
            </div>
            {!beide && <p className="az-hinweis" id="abo-zustimmung-hinweis">{T('Setz bitte beide Häkchen, dann geht es weiter zur Kasse.', 'Please tick both boxes to continue to checkout.')}</p>}
            <p className="kh-klein">{T('Zahlungsdaten und E-Mail-Adresse gibst du in der sicheren Stripe-Kasse ein. Endpreise, keine Umsatzsteuer ausgewiesen (Kleinunternehmer, § 19 UStG).',
              'You enter your payment details and email address in the secure Stripe checkout. Final prices, no VAT charged (small business, § 19 UStG).')}</p>
            {/* § 7 Abs. 3 Nr. 4 UWG: Hinweis auf das Widerspruchsrecht schon beim Kauf (Abmeldelink in jeder Rundmail) */}
            <p className="kh-klein">{T('Nach dem Kauf schicken wir dir ab und zu Neuigkeiten zu ähnlichen Werkzeugen per Mail. Du kannst dem jederzeit widersprechen, über den Link in jeder dieser Mails oder an contact@davidmedia.de.',
              'After your purchase we occasionally email you news about similar tools. You can object at any time, via the link in every such email or at contact@davidmedia.de.')}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
